import { Injectable, signal, type WritableSignal } from '@angular/core';
import { TaskType, type Task } from '@go-modules/interfaces/task';

interface InstructionDraft {
	taskType: TaskType;
	text: string;
}

@Injectable()
export class ActivityPreviewInlineTaskInstructionStateService {
	private readonly openedTaskInstructionTypes = signal<ReadonlySet<TaskType>>(new Set());
	private readonly editingTaskInstructionTypes = signal<ReadonlySet<TaskType>>(new Set());
	private readonly instructionDraft = signal<InstructionDraft | null>(null);

	public isAddingTaskInstruction (task: Task | null | undefined): boolean {
		return this.hasTaskType(this.openedTaskInstructionTypes(), task);
	}

	public isEditingTaskInstruction (task: Task | null | undefined): boolean {
		return this.hasTaskType(this.editingTaskInstructionTypes(), task);
	}

	public openTaskInstruction (task: Task): void {
		this.updateTaskTypes(this.openedTaskInstructionTypes, task, true);
	}

	public closeTaskInstruction (task: Task): void {
		this.updateTaskTypes(this.openedTaskInstructionTypes, task, false);
		this.updateTaskTypes(this.editingTaskInstructionTypes, task, false);

		if (this.instructionDraft()?.taskType === task.type) {
			this.instructionDraft.set(null);
		}
	}

	public startEditingTaskInstruction (task: Task | null | undefined, text: string): void {
		if (task == null) {
			return;
		}

		this.setInstructionDraft(task, text);
		this.updateTaskTypes(this.editingTaskInstructionTypes, task, true);
	}

	public setInstructionDraft (task: Task | null | undefined, text: string): void {
		if (task == null) {
			return;
		}

		this.instructionDraft.set({ taskType: task.type, text });
	}

	public getInstructionDraftText (task: Task | null | undefined, fallbackText: string): string {
		const draft = this.instructionDraft();
		return draft != null && task != null && draft.taskType === task.type
			? draft.text
			: fallbackText;
	}

	public syncActiveTasks (tasks: Task[]): void {
		const activeTaskTypes = new Set(
			tasks
				.filter((task) => task.deleted_at == null)
				.map((task) => task.type)
		);

		this.keepOnlyActiveTaskTypes(this.openedTaskInstructionTypes, activeTaskTypes);
		this.keepOnlyActiveTaskTypes(this.editingTaskInstructionTypes, activeTaskTypes);

		const draft = this.instructionDraft();
		if (draft != null && !activeTaskTypes.has(draft.taskType)) {
			this.instructionDraft.set(null);
		}
	}

	private hasTaskType (taskTypes: ReadonlySet<TaskType>, task: Task | null | undefined): boolean {
		return task != null && taskTypes.has(task.type);
	}

	private updateTaskTypes (
		taskTypesSignal: WritableSignal<ReadonlySet<TaskType>>,
		task: Task,
		shouldInclude: boolean
	): void {
		taskTypesSignal.update((taskTypes) => {
			if (taskTypes.has(task.type) === shouldInclude) {
				return taskTypes;
			}

			const nextTaskTypes = new Set(taskTypes);
			if (shouldInclude) {
				nextTaskTypes.add(task.type);
			} else {
				nextTaskTypes.delete(task.type);
			}
			return nextTaskTypes;
		});
	}

	private keepOnlyActiveTaskTypes (
		taskTypesSignal: WritableSignal<ReadonlySet<TaskType>>,
		activeTaskTypes: ReadonlySet<TaskType>
	): void {
		const taskTypes = taskTypesSignal();
		const nextTaskTypes = [...taskTypes].filter((taskType) => activeTaskTypes.has(taskType));

		if (nextTaskTypes.length !== taskTypes.size) {
			taskTypesSignal.set(new Set(nextTaskTypes));
		}
	}
}
